import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, useMapEvents, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import { Text, Stack, Paper } from '../../../components/gab/GabComponents';

// Fix default marker icons
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Position {
  lat: number;
  lng: number;
}

function LocationMarker({ position, onLocationSelect }: { position?: Position | null; onLocationSelect: (pos: Position) => void }) {
  useMapEvents({
    click(e) {
      onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });

  return position ? <Marker position={[position.lat, position.lng]} /> : null;
}

interface Props {
  value?: Position | null;
  onChange: (pos: Position) => void;
  height?: number;
}

function validCoords(pos?: Position | null): pos is Position {
  return pos != null && (pos.lat !== 0 || pos.lng !== 0);
}

export default function MapPicker({ value, onChange, height = 300 }: Props) {
  const hasCoords = validCoords(value);
  const center: [number, number] = hasCoords
    ? [value!.lat, value!.lng]
    : [33.749, -84.388]; // Default: Atlanta

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">Click on the map to set location</Text>
      <Paper withBorder radius="sm" style={{ overflow: 'hidden' }}>
        <MapContainer
          center={center}
          zoom={hasCoords ? 14 : 4}
          style={{ height, width: '100%' }}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                attribution='&copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Topo">
              <TileLayer
                attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="USGS Topo">
              <TileLayer
                attribution='&copy; USGS'
                url="https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          <LocationMarker position={value} onLocationSelect={onChange} />
        </MapContainer>
      </Paper>
      {hasCoords && (
        <Text size="xs" c="dimmed">{value!.lat.toFixed(6)}, {value!.lng.toFixed(6)}</Text>
      )}
    </Stack>
  );
}
